import clsx from 'clsx';
import { type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { BreadcrumbLink } from '~/packages/eds/src/components/Breadcrumb/types';
import type {
  BreadcrumbResolver,
  BreadcrumbResolverResult,
} from '~/routing/types';

import { useSidebarNav } from '~/features/app/SidebarNavContext';
import { Breadcrumb } from '~/packages/eds/src/components/Breadcrumb/Breadcrumb';
import { Icon } from '~/packages/eds/src/components/Icon/Icon';

import './AppBreadcrumb.css';
import { useAppBreadcrumbSlot } from './AppBreadcrumbSlotContext';
import { AppHeaderActions } from './AppHeaderActions';
import {
  type BreadcrumbItem,
  useRouteBreadcrumbs,
} from './useRouteBreadcrumbs';

export const APP_BREADCRUMB_HEIGHT = 48;

const messages = defineMessages({
  showSidebar: {
    id: 'AppBreadcrumb.showSidebar',
    defaultMessage: 'Show sidebar',
    description:
      'Tooltip and a11y label for the mobile sidebar toggle when the sidebar is collapsed.',
  },
  loadingCrumb: {
    id: 'AppBreadcrumb.loadingCrumb',
    defaultMessage: 'Loading',
    description:
      'Accessible label for a breadcrumb whose name is still loading.',
  },
});

// Until a dynamic crumb's label resolves, hand EDS a loading crumb
// (`isLoading`) so it renders its own shimmer placeholder.
function toBreadcrumbLink(
  crumb: BreadcrumbItem,
  result: BreadcrumbResolverResult | undefined,
): BreadcrumbLink {
  const path = crumb.pathname ?? '';
  if (crumb.kind === 'static') {
    return { path, text: crumb.text };
  }
  return !result || result.isLoading || result.label === null
    ? { path, isLoading: true }
    : { path, text: result.label };
}

export const AppBreadcrumb = () => {
  const intl = useIntl();
  const trail = useRouteBreadcrumbs();
  const { setNode } = useAppBreadcrumbSlot();
  const { isMobile, isCollapsed, toggleCollapsed } = useSidebarNav();

  // A deferred route's page portals its own trail into the slot below, so this
  // bar renders the slot and nothing else. Treat the bar as non-empty either
  // way: the crumbs are coming, just from the page.
  const isDeferred = trail.mode === 'deferred';
  const crumbs = trail.mode === 'resolved' ? trail.crumbs : [];
  const hasCrumbs = isDeferred || crumbs.length > 0;

  return (
    <div
      className={clsx('app-breadcrumb-bar', {
        // The brand lockup overflows the collapsed rail into this bar — inset
        // the crumbs so they start clear of it.
        'app-breadcrumb-bar--sidebar-collapsed': isCollapsed && !isMobile,
      })}
    >
      {/* Mobile only: once the sidebar is collapsed it's aria-hidden and
          slides off-screen, taking its own toggle button with it — this is
          the only remaining way to reopen it. */}
      {isMobile && isCollapsed && (
        <button
          type="button"
          title={intl.formatMessage(messages.showSidebar)}
          aria-label={intl.formatMessage(messages.showSidebar)}
          className="breadcrumb-hamburger"
          onClick={toggleCollapsed}
        >
          <Icon icon="left-panel-open" />
        </button>
      )}

      {/* Separates the brand lockup — which spills out of the collapsed rail
          into this bar — from the crumbs that follow it. */}
      {isCollapsed && !isMobile && hasCrumbs && (
        <span aria-hidden="true" className="breadcrumb-brand-divider" />
      )}

      {hasCrumbs && (
        <div className="app-breadcrumb-nav" ref={isDeferred ? setNode : null}>
          {!isDeferred && (
            <BreadcrumbTrail
              crumbs={crumbs}
              loadingLabel={intl.formatMessage(messages.loadingCrumb)}
            />
          )}
        </div>
      )}

      <AppHeaderActions />
    </div>
  );
};

// Resolves a single dynamic crumb's label with its resolver hook, then renders
// `children` with the result. Keyed by the caller so it remounts when the
// resolver changes, keeping its one hook call valid across navigations.
function DynamicCrumbResolver({
  resolver,
  children,
}: {
  resolver: BreadcrumbResolver;
  children: (result: BreadcrumbResolverResult) => ReactNode;
}) {
  const useResolver = resolver;
  return <>{children(useResolver())}</>;
}

interface BreadcrumbTrailProps {
  crumbs: BreadcrumbItem[];
  loadingLabel: string;
}

/**
 * A trail can carry any number of dynamic crumbs, each backed by its own
 * resolver hook. React forbids calling a route-dependent number of hooks in a
 * loop, so each dynamic crumb is resolved by its own `DynamicCrumbResolver`,
 * nested into a chain that threads results down to a single EDS `Breadcrumb`.
 * Labels resolve during render, so a crumb shows EDS's loading placeholder only
 * while its query is genuinely pending.
 */
function BreadcrumbTrail({ crumbs, loadingLabel }: BreadcrumbTrailProps) {
  const resolveFrom = (
    index: number,
    results: Record<number, BreadcrumbResolverResult>,
  ): ReactNode => {
    if (index >= crumbs.length) {
      return (
        <Breadcrumb
          links={crumbs.map((crumb, i) => toBreadcrumbLink(crumb, results[i]))}
          loadingLabel={loadingLabel}
        />
      );
    }

    const crumb = crumbs[index];
    if (crumb.kind !== 'dynamic') {
      return resolveFrom(index + 1, results);
    }

    return (
      <DynamicCrumbResolver
        key={`${index}:${crumb.resolver.name}`}
        resolver={crumb.resolver}
      >
        {(result) => resolveFrom(index + 1, { ...results, [index]: result })}
      </DynamicCrumbResolver>
    );
  };

  return <>{resolveFrom(0, {})}</>;
}
